import { createStore, type Store } from './store';

/**
 * Mic + system audio mixed into a single PCM stream.
 *
 * The graph is: sources -> gains -> mix bus -> AudioWorklet -> (silent sink).
 * The worklet hands back fixed 1024-sample blocks, which line up exactly with
 * AAC frames and give sample-accurate timestamps for the muxer. Nothing is
 * routed to the speakers, so capturing system audio never feeds back.
 */

const BLOCK_SIZE = 1024;

const WORKLET_SOURCE = `
class OpaleyeCapture extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const { blockSize = 1024 } = (options && options.processorOptions) || {};
    this.blockSize = blockSize;
    this.channels = 2;
    this.block = null;
    this.filled = 0;
    this.blockIndex = 0;
  }
  process(inputs, outputs) {
    const input = inputs[0];
    const output = outputs[0];
    const channelCount = output.length;
    for (let c = 0; c < channelCount; c++) {
      const source = input[c] || input[0];
      if (source) output[c].set(source);
      else output[c].fill(0);
    }
    if (!this.block || this.block.length !== channelCount) {
      this.block = [];
      for (let c = 0; c < channelCount; c++) this.block.push(new Float32Array(this.blockSize));
      this.filled = 0;
    }
    let offset = 0;
    const frameLength = output[0].length;
    while (offset < frameLength) {
      const take = Math.min(this.blockSize - this.filled, frameLength - offset);
      for (let c = 0; c < channelCount; c++) {
        this.block[c].set(output[c].subarray(offset, offset + take), this.filled);
      }
      this.filled += take;
      offset += take;
      if (this.filled === this.blockSize) {
        const planes = new Float32Array(this.blockSize * channelCount);
        for (let c = 0; c < channelCount; c++) planes.set(this.block[c], c * this.blockSize);
        this.port.postMessage(
          { frameIndex: this.blockIndex, channels: channelCount, length: this.blockSize, data: planes },
          [planes.buffer],
        );
        this.blockIndex += this.blockSize;
        this.filled = 0;
      }
    }
    return true;
  }
}
registerProcessor('opaleye-capture', OpaleyeCapture);
`;

export interface AudioBlock {
  /** Planar PCM: channel 0 samples, then channel 1, ... */
  data: Float32Array<ArrayBuffer>;
  frameIndex: number;
  channels: number;
  sampleRate: number;
  length: number;
}

export interface MixerOptions {
  micStream: MediaStream | null;
  systemStream: MediaStream | null;
  micGain: number;
  systemGain: number;
  sampleRate?: number;
  onBlock: (block: AudioBlock) => void;
  onError: (error: Error) => void;
}

export interface MeterLevels {
  mic: number;
  system: number;
  mix: number;
}

export class AudioMixer {
  readonly context: AudioContext;
  readonly meters: Store<MeterLevels>;

  private readonly micGainNode: GainNode | null;
  private readonly systemGainNode: GainNode | null;
  private readonly analysers: Array<{ analyser: AnalyserNode; key: keyof MeterLevels }> = [];
  private readonly buffer = new Float32Array(2048);
  private readonly sources: MediaStreamAudioSourceNode[] = [];
  private readonly worklet: AudioWorkletNode;
  private readonly sink: GainNode;
  private readonly streamDestination: MediaStreamAudioDestinationNode;
  private timer: ReturnType<typeof setInterval> | null = null;

  private constructor(context: AudioContext, worklet: AudioWorkletNode, options: MixerOptions) {
    this.context = context;
    this.worklet = worklet;
    this.meters = createStore<MeterLevels>({ mic: 0, system: 0, mix: 0 });

    const mixBus = context.createGain();
    mixBus.channelCount = 2;
    mixBus.channelCountMode = 'explicit';
    mixBus.channelInterpretation = 'speakers';

    const makeSource = (stream: MediaStream | null, gainValue: number) => {
      if (!stream || stream.getAudioTracks().length === 0) return null;
      const source = context.createMediaStreamSource(stream);
      const gain = context.createGain();
      gain.gain.value = gainValue;
      source.connect(gain);
      gain.connect(mixBus);
      this.sources.push(source);
      return { source, gain };
    };

    const mic = makeSource(options.micStream, options.micGain);
    const system = makeSource(options.systemStream, options.systemGain);
    this.micGainNode = mic?.gain ?? null;
    this.systemGainNode = system?.gain ?? null;

    mixBus.connect(worklet);

    const addAnalyser = (node: AudioNode, key: keyof MeterLevels) => {
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      node.connect(analyser);
      this.analysers.push({ analyser, key });
    };
    if (mic) addAnalyser(mic.gain, 'mic');
    if (system) addAnalyser(system.gain, 'system');
    addAnalyser(mixBus, 'mix');

    // A silent sink keeps the graph pulling without monitoring anything.
    this.sink = context.createGain();
    this.sink.gain.value = 0;
    this.sink.connect(context.destination);
    for (const { analyser } of this.analysers) analyser.connect(this.sink);

    // Some browsers only render a graph that terminates in a live destination.
    this.streamDestination = context.createMediaStreamDestination();
    worklet.connect(this.streamDestination);
    worklet.connect(this.sink);

    worklet.port.onmessage = (event: MessageEvent) => {
      const payload = event.data as {
        data: Float32Array<ArrayBuffer>;
        frameIndex: number;
        channels: number;
        length: number;
      };
      options.onBlock({
        data: payload.data,
        frameIndex: payload.frameIndex,
        channels: payload.channels,
        length: payload.length,
        sampleRate: context.sampleRate,
      });
    };
    worklet.onprocessorerror = () => {
      options.onError(new Error('The audio capture processor crashed.'));
    };
  }

  static async create(options: MixerOptions): Promise<AudioMixer | null> {
    const hasMic = Boolean(options.micStream?.getAudioTracks().length);
    const hasSystem = Boolean(options.systemStream?.getAudioTracks().length);
    if (!hasMic && !hasSystem) return null;

    const sampleRate = options.sampleRate ?? 48_000;
    const context = new AudioContext({ sampleRate, latencyHint: 'interactive' });
    try {
      const blobUrl = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }));
      try {
        await context.audioWorklet.addModule(blobUrl);
      } finally {
        URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      await context.close();
      throw new Error(
        `Audio capture needs AudioWorklet, which this browser did not provide. (${String(error)})`,
      );
    }

    const worklet = new AudioWorkletNode(context, 'opaleye-capture', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      channelCount: 2,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers',
      processorOptions: { blockSize: BLOCK_SIZE },
    });

    const mixer = new AudioMixer(context, worklet, options);
    mixer.startMetering();
    return mixer;
  }

  get hasMic(): boolean {
    return this.micGainNode !== null;
  }

  get hasSystem(): boolean {
    return this.systemGainNode !== null;
  }

  setGains(mic: number, system: number): void {
    if (this.micGainNode) this.micGainNode.gain.value = mic;
    if (this.systemGainNode) this.systemGainNode.gain.value = system;
  }

  private startMetering(): void {
    if (this.timer !== null) return;
    this.timer = setInterval(() => {
      const patch: Partial<MeterLevels> = {};
      for (const { analyser, key } of this.analysers) {
        analyser.getFloatTimeDomainData(this.buffer.subarray(0, analyser.fftSize));
        let peak = 0;
        for (let i = 0; i < analyser.fftSize; i += 1) {
          const value = Math.abs(this.buffer[i]);
          if (value > peak) peak = value;
        }
        patch[key] = Math.min(1, peak);
      }
      this.meters.setState(patch);
    }, 66);
  }

  async close(): Promise<void> {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.worklet.port.onmessage = null;
    for (const source of this.sources) {
      try {
        source.disconnect();
      } catch {
        // already disconnected
      }
    }
    try {
      this.worklet.disconnect();
      this.sink.disconnect();
      this.streamDestination.disconnect();
    } catch {
      // already disconnected
    }
    if (this.context.state !== 'closed') await this.context.close();
  }
}

/**
 * Standalone level meter so the mic can be checked before a take starts.
 * Replaced by the mixer's own metering once recording begins.
 */
export class MicMonitor {
  readonly meters: Store<MeterLevels>;
  private readonly context: AudioContext;
  private readonly source: MediaStreamAudioSourceNode;
  private readonly sink: GainNode;
  private readonly timer: ReturnType<typeof setInterval>;

  private constructor(
    context: AudioContext,
    source: MediaStreamAudioSourceNode,
    sink: GainNode,
    timer: ReturnType<typeof setInterval>,
  ) {
    this.context = context;
    this.source = source;
    this.sink = sink;
    this.timer = timer;
    this.meters = createStore<MeterLevels>({ mic: 0, system: 0, mix: 0 });
  }

  static async create(stream: MediaStream, sampleRate = 48_000): Promise<MicMonitor> {
    if (stream.getAudioTracks().length === 0) {
      throw new Error('That microphone stream has no audio track.');
    }
    const context = new AudioContext({ sampleRate, latencyHint: 'interactive' });
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    const sink = context.createGain();
    sink.gain.value = 0;
    source.connect(analyser);
    analyser.connect(sink);
    sink.connect(context.destination);

    const buffer = new Float32Array(analyser.fftSize);
    let monitor: MicMonitor;
    const timer = setInterval(() => {
      analyser.getFloatTimeDomainData(buffer);
      let peak = 0;
      for (let i = 0; i < buffer.length; i += 1) {
        const value = Math.abs(buffer[i]);
        if (value > peak) peak = value;
      }
      monitor?.meters.setState({ mic: Math.min(1, peak), system: 0, mix: 0 });
    }, 66);
    monitor = new MicMonitor(context, source, sink, timer);
    return monitor;
  }

  async close(): Promise<void> {
    clearInterval(this.timer);
    try {
      this.source.disconnect();
      this.sink.disconnect();
    } catch {
      // already disconnected
    }
    if (this.context.state !== 'closed') await this.context.close();
  }
}
